import { formatCurrency, toNumber, getAdjustmentBreakdown } from './leasingCalculations'

// Listepris tooltip
export const getListeprisTooltip = (result: any): string => {
  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Listepris</div>
      <div class="text-sm opacity-90">
        Bilens listepris inkl. mva., levert Oslo-området (ekskl. utstyr)
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium">${formatCurrency(result.CarPrice)}</span>
      </div>
      ${result.CarPriceURL ? `
      <div class="mt-2 pt-2 border-t border-gray-700 text-xs opacity-75">
        Kilde: ${result.CarPriceURL}
      </div>
      ` : ''}
    </div>
  `
}

// Startleie + Etableringsgebyr tooltip
export const getInitialTotalTooltip = (result: any): string => {
  const initialPay = toNumber(result.InitialPay)
  const initialFee = toNumber(result.InitialFee)
  const total = initialPay + initialFee

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Startleie + Etableringsgebyr</div>
      <div class="text-sm opacity-90">
        Totalt innbetalt beløp ved oppstart av leasingavtalen
      </div>
      <div class="mt-2 text-xs space-y-1">
        <div class="opacity-80">Startleie: ${formatCurrency(initialPay)}</div>
        <div class="opacity-80">Etableringsgebyr: ${formatCurrency(initialFee)}</div>
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium">${formatCurrency(total)}</span>
      </div>
    </div>
  `
}

// Månedspris kampanje tooltip
export const getLeasePriceTooltip = (result: any): string => {
  const leasePrice = toNumber(result.LeasePrice)

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Månedspris kampanje</div>
      <div class="text-sm opacity-90">
        Annonsert månedlig leasingpris i kampanjen (ekskl. startleie og etableringsgebyr)
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium">${formatCurrency(leasePrice)}/mnd</span>
      </div>
    </div>
  `
}

// Leasingpris 1 tooltip
export const getLeasingpris1Tooltip = (result: any): string => {
  const leasePrice = toNumber(result.LeasePrice)
  const duration = toNumber(result.LeaseDurationMonths)
  const initialPay = toNumber(result.InitialPay)
  const initialFee = toNumber(result.InitialFee)
  const totalLeasePrice1 = toNumber(result.TotalLeasePrice1)

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Totalpris mnd (kampanje)</div>
      <div class="text-sm opacity-90">
        Annonsert kampanjepris per måned (uten justeringer)
      </div>
      <div class="mt-2 text-xs space-y-1">
        <div class="font-mono opacity-80">Formel:</div>
        <div class="font-mono opacity-80">
          (Månedspris × Periode + Startleie + Etablering) ÷ Periode
        </div>
        <div class="font-mono opacity-80">
          (${formatCurrency(leasePrice)} × ${duration} + ${formatCurrency(initialPay)} + ${formatCurrency(initialFee)}) ÷ ${duration}
        </div>
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium">${formatCurrency(totalLeasePrice1)}/mnd</span>
      </div>
    </div>
  `
}

// Totalpris 1 tooltip
export const getTotalpris1Tooltip = (result: any): string => {
  const totalLeasePrice1 = toNumber(result.TotalLeasePrice1)
  const duration = toNumber(result.LeaseDurationMonths)
  const totalPrice1 = toNumber(result.TotalPrice1)

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Totalpris 1</div>
      <div class="text-sm opacity-90">
        Total månedspris: Reell total kostnad etter fordeling av startleie og etableringsgebyr
      </div>
      <div class="mt-2 text-xs space-y-1">
        <div class="font-mono opacity-80">Formel:</div>
        <div class="font-mono opacity-80">
          Leasingpris 1 × Leaseperiode
        </div>
        <div class="font-mono opacity-80">
          ${formatCurrency(totalLeasePrice1)} × ${duration} mnd
        </div>
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium">${formatCurrency(totalPrice1)}</span>
      </div>
    </div>
  `
}

// Individual adjustment tooltips
export const getMetallicTooltip = (result: any): string => {
  const value = toNumber(result.AdjustMetallic)
  if (value === 0) {
    return `
      <div class="space-y-1">
        <div class="font-semibold">Metallisk lakk</div>
        <div class="text-sm opacity-90">Ingen justering</div>
      </div>
    `
  }

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Metallic lakk</div>
      <div class="text-sm opacity-90">
        Metallic-kostnad finansiert over leaseperiode
      </div>
      <div class="mt-2 text-xs opacity-80">
        Beregnes som en annuitet basert på kostpris for metallic og en restverdi på 50% etter 36 måneder.
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium ${value > 0 ? 'text-red-400' : 'text-green-400'}">
          ${value > 0 ? '+' : ''}${formatCurrency(value)}/mnd
        </span>
      </div>
    </div>
  `
}

export const getServiceTooltip = (result: any): string => {
  const value = toNumber(result.AdjustService)
  if (value === 0) {
    return `
      <div class="space-y-1">
        <div class="font-semibold">Servicepakke</div>
        <div class="text-sm opacity-90">Ingen justering</div>
      </div>
    `
  }

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Servicepakke</div>
      <div class="text-sm opacity-90">
        Verdi av inkludert vedlikehold og service
      </div>
      <div class="mt-2 text-xs opacity-80">
        Beregnes som prosentandel av bilens pris (p.t. 0.051% per måned)
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium ${value > 0 ? 'text-red-400' : 'text-green-400'}">
          ${value > 0 ? '+' : ''}${formatCurrency(value)}/mnd
        </span>
      </div>
    </div>
  `
}

export const getTowingHitchTooltip = (result: any): string => {
  const value = toNumber(result.AdjustTowingHitch)
  if (value === 0) {
    return `
      <div class="space-y-1">
        <div class="font-semibold">Hengerfeste</div>
        <div class="text-sm opacity-90">Ingen justering</div>
      </div>
    `
  }

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Hengerfeste</div>
      <div class="text-sm opacity-90">
        Verdi av inkludert hengerfeste
      </div>
      <div class="mt-2 text-xs opacity-80">
        Beregnes som prosentandel av bilens pris (p.t. 0,027% per måned)
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium ${value > 0 ? 'text-red-400' : 'text-green-400'}">
          ${value > 0 ? '+' : ''}${formatCurrency(value)}/mnd
        </span>
      </div>
    </div>
  `
}

export const getOppCostTooltip = (result: any): string => {
  const value = toNumber(result.AdjustOppCost)
  const initialPay = toNumber(result.InitialPay)
  const oppCostRate = result.InitialFeeOppCost ? toNumber(result.InitialFeeOppCost) : null

  if (value === 0) {
    return `
      <div class="space-y-1">
        <div class="font-semibold">Alternativkostnad</div>
        <div class="text-sm opacity-90">Ingen startleie - ingen alternativkostnad</div>
      </div>
    `
  }

  const rateDisplay = oppCostRate !== null
    ? `<div class="text-xs opacity-80">
        Rente brukt: ${(oppCostRate * 100).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})}% (etter skatt)
      </div>`
    : ''

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Alternativkostnad</div>
      <div class="text-sm opacity-90">
        Finansiell kostnad av kapital låst i startleie
      </div>
      <div class="mt-2 text-xs opacity-80">
        Startleie: ${formatCurrency(initialPay)}
      </div>
      ${rateDisplay}
      <div class="text-xs opacity-80">
        Er tapte renteinntekter på innbetalt beløp etter skatt.
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium ${value > 0 ? 'text-red-400' : 'text-green-400'}">
          ${value > 0 ? '+' : ''}${formatCurrency(value)}/mnd
        </span>
      </div>
    </div>
  `
}

export const getMileageTooltip = (result: any): string => {
  const value = toNumber(result.AdjustMileage)
  const mileage = toNumber(result.Mileage)
  
  if (value === 0) {
    return `
      <div class="space-y-1">
        <div class="font-semibold">Kjørelengde</div>
        <div class="text-sm opacity-90">Standard kjørelengde - ingen justering</div>
        <div class="text-xs opacity-80 mt-1">${mileage.toLocaleString('nb-NO')} km/år</div>
      </div>
    `
  }

  const isLow = value < 0

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Lav kjørelengde</div>
      <div class="text-sm opacity-90">
        ${isLow ? 'Fradrag for færre kilometer' : 'Tillegg for flere kilometer'}
      </div>
      <div class="mt-2 text-xs opacity-80">
        Inkludert kjørelengde totalt: ${mileage.toLocaleString('nb-NO')} km
      </div>
      <div class="text-xs opacity-80">
        Under standard for dette segmentet
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium ${value > 0 ? 'text-red-400' : 'text-green-400'}">
          ${value > 0 ? '+' : ''}${formatCurrency(value)}/mnd
        </span>
      </div>
    </div>
  `
}

export const getHighMileageTooltip = (result: any): string => {
  const value = toNumber(result.AdjustHighMileage)
  const mileage = toNumber(result.Mileage)
  
  if (value === 0) {
    return `
      <div class="space-y-1">
        <div class="font-semibold">Høy kjørelengde</div>
        <div class="text-sm opacity-90">Ingen justering</div>
      </div>
    `
  }

  const isReduction = value < 0

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Høy kjørelengde</div>
      <div class="text-sm opacity-90">
        ${isReduction ? 'Fradrag for høy kjørelengde' : 'Tillegg for høy kjørelengde'}
      </div>
      <div class="mt-2 text-xs opacity-80">
        Inkludert kjørelengde totalt: ${mileage.toLocaleString('nb-NO')} km
      </div>
      <div class="text-xs opacity-80">
        Over standard for dette bilsegmentet
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium ${value > 0 ? 'text-red-400' : 'text-green-400'}">
          ${value > 0 ? '+' : ''}${formatCurrency(value)}/mnd
        </span>
      </div>
    </div>
  `
}

export const getManualCorrectionTooltip = (result: any): string => {
  const value = toNumber(result.AdjustManualCorrection)
  const comment = result.CommentManualCorrection
  
  if (value === 0) {
    return `
      <div class="space-y-1">
        <div class="font-semibold">Manuell korreksjon</div>
        <div class="text-sm opacity-90">Ingen manuell justering</div>
      </div>
    `
  }

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Manuell korreksjon</div>
      <div class="text-sm opacity-90">
        Brukerdefinert justering
      </div>
      ${comment ? `
        <div class="mt-2 text-xs opacity-80 italic">
          "${comment}"
        </div>
      ` : ''}
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium ${value > 0 ? 'text-red-400' : 'text-green-400'}">
          ${value > 0 ? '+' : ''}${formatCurrency(value)}/mnd
        </span>
      </div>
    </div>
  `
}

// "Justeringer" tooltip (#707): the six adjustment-column tooltips, unchanged
// — they have passed QA — in a two-column grid read row by row in the old
// column order. Stacked in one column they ran up to ~750 px tall. The km
// block is picked the same way the old "Just: Km" cell picked it.
export const getAdjustmentsTooltip = (result: { AdjustHighMileage: string | number | null }): string => {
  const mileageTooltip = toNumber(result.AdjustHighMileage) !== 0
    ? getHighMileageTooltip(result)
    : getMileageTooltip(result)

  const blocks = [
    getOppCostTooltip(result),
    getMetallicTooltip(result),
    getServiceTooltip(result),
    getTowingHitchTooltip(result),
    mileageTooltip,
    getManualCorrectionTooltip(result),
  ]
  return `<div class="grid grid-cols-2 gap-x-6 gap-y-3">${blocks.map(b => `<div>${b}</div>`).join('')}</div>`
}

// Leasingpris 2 tooltip
export const getLeasingpris2Tooltip = (result: any): string => {
  const totalLeasePrice1 = toNumber(result.TotalLeasePrice1)
  const totalLeasePrice2 = toNumber(result.TotalLeasePrice2)
  const adjustments = getAdjustmentBreakdown(result)

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Totalpris mnd (justert)</div>
      <div class="text-sm opacity-90">
        Totalpris mnd (kampanje) justert for alle relevante justeringer
      </div>
      <div class="mt-2 text-xs space-y-1">
        <div class="opacity-80">Basis: ${formatCurrency(totalLeasePrice1)}/mnd</div>
        ${adjustments.length > 0 ? `
          <div class="mt-2 space-y-1">
            <div class="opacity-80 font-medium">Justeringer:</div>
            ${adjustments.map(adj => `
              <div class="flex justify-between opacity-80">
                <span>• ${adj.label}:</span>
                <span class="${adj.value > 0 ? 'text-red-400' : 'text-green-400'}">
                  ${adj.value > 0 ? '+' : ''}${formatCurrency(adj.value)}
                </span>
              </div>
            `).join('')}
          </div>
        ` : '<div class="opacity-80">Ingen justeringer</div>'}
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <div class="flex justify-between items-center">
          <span class="font-medium">Total:</span>
          <span class="font-semibold text-lg">${formatCurrency(totalLeasePrice2)}/mnd</span>
        </div>
      </div>
    </div>
  `
}

// Totalpris 2 tooltip
export const getTotalpris2Tooltip = (result: any): string => {
  const totalLeasePrice2 = toNumber(result.TotalLeasePrice2)
  const duration = toNumber(result.LeaseDurationMonths)
  const totalPrice2 = toNumber(result.TotalPrice2)

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Totalpris 2</div>
      <div class="text-sm opacity-90">
        Total leasingkostnad over hele perioden
      </div>
      <div class="mt-2 text-xs space-y-1">
        <div class="font-mono opacity-80">Formel:</div>
        <div class="font-mono opacity-80">
          Totalpris mnd (justert) × Leaseperiode
        </div>
        <div class="font-mono opacity-80">
          ${formatCurrency(totalLeasePrice2)} × ${duration} mnd
        </div>
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium">${formatCurrency(totalPrice2)}</span>
      </div>
    </div>
  `
}

// MonthlyLeaseFactor tooltip
export const getMonthlyLeaseFactorTooltip = (result: any): string => {
  if (!result.MonthlyLeaseFactor || !result.CarPrice || !result.TotalLeasePrice2) {
    return `
      <div class="space-y-2">
        <div class="font-semibold text-base">Månedsfaktor</div>
        <div class="text-sm opacity-90">
          Beregning ikke tilgjengelig
        </div>
      </div>
    `
  }

  const factor = toNumber(result.MonthlyLeaseFactor)
  const factorPercent = factor.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})
  const totalLeasePrice2 = toNumber(result.TotalLeasePrice2)
  const carPrice = toNumber(result.CarPrice)

  return `
    <div class="space-y-2">
      <div class="font-semibold text-base">Månedsfaktor</div>
      <div class="text-sm opacity-90">
        Månedlig totalpris (justert) i prosent av bilens listepris
      </div>
      <div class="mt-2 text-xs space-y-1">
        <div class="font-mono opacity-80">Formel:</div>
        <div class="font-mono opacity-80">
          (Totalpris mnd (justert) ÷ Listepris) × 100
        </div>
        <div class="font-mono opacity-80">
          (${formatCurrency(totalLeasePrice2)} ÷ ${formatCurrency(carPrice)}) × 100
        </div>
      </div>
      <div class="mt-2 pt-2 border-t border-gray-700">
        <span class="font-medium">${factorPercent}</span>
      </div>
      <div class="mt-2 text-xs opacity-80 italic">
        Lavere faktor = mer konkurransedyktig leasing. Typisk verdi: 1.0-2.0
      </div>
    </div>
  `
}
